import { EMPTY_STRING_ARRAY } from "../constants";
import { areFieldListsEqual, buildCompositeKey } from "../utils/compositeKey";
import { toFiniteNumber } from "../utils/toFiniteNumber";
import type { DataRow, GroupFieldTemplate } from "../types";

export interface AggregateRowsArgs {
  data: DataRow[];
  keyFields: string[];
  groupBy: string[];
  groupKeysByRow?: string[];
  aggregationKeys: string[];
  averageKeys: string[];
  averageDuplicationKeys?: string[];
  priceKey: string;
  volumeKey: string;
  orderedTemplates: GroupFieldTemplate[];
  templatesByGroup: Map<string, GroupFieldTemplate>;
}

export function aggregateRows({
  data,
  keyFields,
  groupBy,
  groupKeysByRow,
  aggregationKeys,
  averageKeys,
  averageDuplicationKeys,
  priceKey,
  volumeKey,
  orderedTemplates,
  templatesByGroup,
}: AggregateRowsArgs): DataRow[] {
  const rowsByKey = new Map<string, DataRow>();
  const aggCount = aggregationKeys.length;
  const avgCount = averageKeys.length;
  const hasAverageKeys = avgCount > 0;
  const averageDuplicationKeyList = averageDuplicationKeys ?? EMPTY_STRING_ARRAY;
  const hasAverageDuplicationKeys = averageDuplicationKeyList.length > 0;
  const keyFieldCount = keyFields.length;
  const dataLength = data.length;
  const reuseGroupKeyAsRowKey =
    groupKeysByRow !== undefined && areFieldListsEqual(keyFields, groupBy);

  for (let rowIndex = 0; rowIndex < dataLength; rowIndex += 1) {
    const row = data[rowIndex];
    const groupKey = groupKeysByRow
      ? groupKeysByRow[rowIndex]
      : buildCompositeKey(row, groupBy);
    const groupTemplate = templatesByGroup.get(groupKey);
    if (!groupTemplate) {
      continue;
    }

    const rowKey = reuseGroupKeyAsRowKey
      ? groupKey
      : buildCompositeKey(row, keyFields);

    let bucket = rowsByKey.get(rowKey);
    if (!bucket) {
      bucket = {} as DataRow;
      for (let kIndex = 0; kIndex < keyFieldCount; kIndex += 1) {
        const field = keyFields[kIndex];
        bucket[field] = row[field];
      }

      rowsByKey.set(rowKey, bucket);
    }

    const aggregationFields = groupTemplate.aggregationFields;
    for (let aggIndex = 0; aggIndex < aggCount; aggIndex += 1) {
      const aggValue = toFiniteNumber(row[aggregationKeys[aggIndex]]);
      if (aggValue !== null) {
        const field = aggregationFields[aggIndex];
        const existing = bucket[field];
        bucket[field] =
          (typeof existing === "number" && Number.isFinite(existing)
            ? existing
            : 0) + aggValue;
      }
    }

    const volumeValue = toFiniteNumber(row[volumeKey]);

    if (volumeValue !== null) {
      const volField = groupTemplate.volumeField;
      const existingVol = bucket[volField];
      bucket[volField] =
        (typeof existingVol === "number" && Number.isFinite(existingVol)
          ? existingVol
          : 0) + volumeValue;

      const priceValue = toFiniteNumber(row[priceKey]);

      if (priceValue !== null) {
        const priceField = groupTemplate.priceField;
        const existingPrice = bucket[priceField];
        bucket[priceField] =
          (typeof existingPrice === "number" && Number.isFinite(existingPrice)
            ? existingPrice
            : 0) +
          priceValue * volumeValue;
      }
    }

    if (hasAverageKeys) {
      const averageDuplicationSuffix = hasAverageDuplicationKeys
        ? buildCompositeKey(row, averageDuplicationKeyList)
        : "";

      const averageFields = groupTemplate.averageFields;
      const averageCountFields = groupTemplate.averageCountFields;
      const averageSetFields = groupTemplate.averageSetFields;

      for (let avgIndex = 0; avgIndex < avgCount; avgIndex += 1) {
        const avgValue = toFiniteNumber(row[averageKeys[avgIndex]]);

        if (avgValue === null) {
          continue;
        }

        const avgField = averageFields[avgIndex];
        const avgCountField = averageCountFields[avgIndex];

        const existingAvg = bucket[avgField];
        bucket[avgField] =
          (typeof existingAvg === "number" && Number.isFinite(existingAvg)
            ? existingAvg
            : 0) + avgValue;

        const existingCount = bucket[avgCountField];
        bucket[avgCountField] =
          (typeof existingCount === "number" && Number.isFinite(existingCount)
            ? existingCount
            : 0) + 1;

        if (hasAverageDuplicationKeys) {
          const setField = averageSetFields[avgIndex];
          let dedupeSet = bucket[setField] as Set<string> | undefined;
          if (!dedupeSet) {
            dedupeSet = new Set<string>();
            bucket[setField] = dedupeSet;
          }

          dedupeSet.add(avgField + "_" + averageDuplicationSuffix);
        }
      }
    }
  }

  const result = Array.from(rowsByKey.values());
  const resultLength = result.length;
  const templateCount = orderedTemplates.length;

  for (let rIndex = 0; rIndex < resultLength; rIndex += 1) {
    const bucket = result[rIndex];
    for (let tIndex = 0; tIndex < templateCount; tIndex += 1) {
      const groupTemplate = orderedTemplates[tIndex];
      const volField = groupTemplate.volumeField;
      const priceField = groupTemplate.priceField;

      const existingVol = bucket[volField];
      const volume =
        typeof existingVol === "number" && Number.isFinite(existingVol)
          ? existingVol
          : 0;
      if (existingVol === undefined) {
        bucket[volField] = 0;
      }

      const existingPrice = bucket[priceField];
      const totalPrice =
        typeof existingPrice === "number" && Number.isFinite(existingPrice)
          ? existingPrice
          : 0;

      if (volume > 0) {
        bucket[priceField] = totalPrice / volume;
      } else {
        bucket[priceField] = 0;
      }

      const aggregationFields = groupTemplate.aggregationFields;
      const aggFieldCount = aggregationFields.length;
      for (let aIndex = 0; aIndex < aggFieldCount; aIndex += 1) {
        const aggField = aggregationFields[aIndex];
        if (bucket[aggField] === undefined) {
          bucket[aggField] = 0;
        }
      }

      if (hasAverageKeys) {
        const averageFields = groupTemplate.averageFields;
        const averageCountFields = groupTemplate.averageCountFields;
        const averageSetFields = groupTemplate.averageSetFields;
        for (let avgIndex = 0; avgIndex < avgCount; avgIndex += 1) {
          const avgField = averageFields[avgIndex];
          const avgCountField = averageCountFields[avgIndex];

          let divisor: number;
          if (hasAverageDuplicationKeys) {
            const setField = averageSetFields[avgIndex];
            const dedupeSet = bucket[setField] as Set<string> | undefined;
            divisor = dedupeSet ? dedupeSet.size : 0;
            if (dedupeSet) {
              delete bucket[setField];
            }
          } else {
            const cntRaw = bucket[avgCountField];
            divisor =
              typeof cntRaw === "number" && Number.isFinite(cntRaw)
                ? cntRaw
                : 0;
          }

          if (bucket[avgCountField] === undefined) {
            bucket[avgCountField] = 0;
          }

          const totalRaw = bucket[avgField];
          const averageTotal =
            typeof totalRaw === "number" && Number.isFinite(totalRaw)
              ? totalRaw
              : 0;

          bucket[avgField] = divisor > 0 ? averageTotal / divisor : 0;
        }
      }
    }
  }

  return result;
}
